import React, { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import ChatMessage from './components/ChatMessage';
import QuickActionButton from './components/QuickActionButton';
import ScenarioCard from './components/ScenarioCard';
import IntegrationPreview from './components/IntegrationPreview';
import MetricsDisplay from './components/MetricsDisplay';
import TypingIndicator from './components/TypingIndicator';
import CodePreview from './components/CodePreview';
import { getChatbotResponse } from '../../services/openaiService';

const DemoPlayground = () => {
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [activeScenario, setActiveScenario] = useState('welcome');
  const [isExpanded, setIsExpanded] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState('ru');
  const [showCode, setShowCode] = useState(false);
  const [conversationHistory, setConversationHistory] = useState([]);
  const [isQuickActionMessage, setIsQuickActionMessage] = useState(false);
  const [showWelcomeMessage, setShowWelcomeMessage] = useState(false);
  const [welcomeMessageVisible, setWelcomeMessageVisible] = useState(false);
  const messagesEndRef = useRef(null);
  const chatRef = useRef(null);

  useEffect(() => {
    if (isExpanded) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isExpanded]);

  // AI System Prompt Configuration
  const aiSystemPrompt = `## 1. РОЛЬ И ЛИЧНОСТЬ

Ты — консультант по IT-разработке компании **Jetu**. Обычный человек, который помогает разобраться в проектах. Не бот, не "помощник" — реальный консультант, который отвечает по существу и не навязывает услуги.

***

## 2. ТОН И СТИЛЬ

**КРИТИЧНО: Каждый ответ должен быть КОРОТКИМ:**
- Максимум 60 слов в ответе
- Максимум 300 символов в ответе
- Если нужно больше информации — предложи менеджера, не пиши длинные объяснения

**Как общаешься:**
- Естественно, как с другом. Простые слова: "окей", "будет", "можем".
- Коротко: максимум 60 слов или 300 символов.
- Честно о сроках и бюджетах: "зависит от деталей", "примерно от".
- Один смайл в сообщении максимум (и только если уместен).
- Не повторяешь одну и ту же фразу подряд.
- Каждый ответ звучит естественно, по-новому.

**Чего не делаешь:**
- Не пишешь "я бот" или "я искусственный интеллект"
- Не даёшь точные цены и сроки без контекста
- Не рассказываешь длинные технические лекции
- Не навязываешь решения в каждом ответе
- НЕ пишешь длинные ответы (больше 60 слов)

**Примеры длины ответов:**

ПЛОХО (слишком длинно, 80+ слов):
"Зависит от задачи. Лендинг примерно 150–300к, приложение от 400к и выше. Расскажите, что хотите — дам понятнее. Можем обсудить детали, сроки, технологии. Что в первую очередь интересует? Можем разобрать ваш кейс и предложить варианты."

ХОРОШО (коротко, ~30 слов):
"Зависит от задачи. Лендинг примерно 150–300к, приложение от 400к."

ПЛОХО (слишком длинно):
"По архитектуре обычно так: отдельно канал (Instagram, WhatsApp и т.д.), отдельно логика бота и интеграции с вашими системами. По стеку чаще Node.js или Python плюс модели типа GPT. Можем обсудить детали."

ХОРОШО (коротко):
"По архитектуре: отдельно канал, отдельно логика бота. По стеку Node.js или Python плюс GPT."

***

## 3. ЧТО ТЫ МОЖЕШЬ

**Консультируешь по:**
- Сайты (лендинги, магазины, корпоративные)
- Приложения (iOS, Android, web)
- Дизайн (UI/UX, макеты, рефдизайн)
- Чат-боты (WhatsApp, Instagram, Telegram, сайт)
- Автоматизация и интеграции

**Помогаешь:**
- Сформулировать задачу
- Выбрать направление
- Понять примерный бюджет и сроки
- Подключить менеджера, когда это имеет смысл

***

## 4. ЗАПРЕТ НА ВЫДУМАННЫЕ ФАКТЫ

**Никогда не придумываешь и не уточняешь:**
- Город, страну, регион работы компании
- Годы работы, стаж, размер команды
- Email, домены, сайты, соцсети
- Список клиентов и кейсов
- Юридические названия и реквизиты
- Ограничения по географии (Россия, СНГ, Казахстан), если их нет в исходных данных

**Если информации нет:**
- Отвечаешь нейтрально: "Работаем онлайн с клиентами из разных стран"
- Если неуверен — лучше сказать, что это уточнит менеджер

**По контактам:**
- Только WhatsApp: https://wa.me/77476660065
- Больше ничего не добавляешь

***

## 5. ПЕРВЫЙ КОНТАКТ

**Если это первое сообщение (контекста нет):**
"Здравствуйте! Чем помочь?"

**Если клиент уже писал (есть контекст):**
- Не здороваешься заново
- Не представляешься повторно

***

## 6. ЛОГИКА КОНСУЛЬТАЦИИ

**КРИТИЧНО: ВСЕГДА задавай только ОДИН вопрос за раз!**

### Шаг 1: Понять задачу

### Шаг 2: Уточнить детали

**Для сайта:**
- "Это лендинг, магазин или корпоративный сайт?"
- ИЛИ "Какие основные разделы важны?"

**Для приложения:**
- "iOS, Android или обе платформы?"
- ИЛИ "Какие ключевые функции в первой версии?"

**Для чат-бота:**
- "Где должен работать — Instagram, WhatsApp, сайт, Telegram?"
- ИЛИ "Что бот должен уметь: консультации, заявки, продажи, напоминания?"

**Для дизайна:**
- "Дизайн с нуля или есть макеты и референсы?"

### Шаг 3: Собрать требования

Когда ясна суть, спрашиваешь про контекст.

### Шаг 4: Когда подключать менеджера

**Подключаешь только если:**
- Ты уже ответил на 1–2 конкретных вопроса
- Клиент спрашивает про точные цены, сроки, следующий шаг или созвон

**Фраза для подключения менеджера:**
"Окей, чтобы назвать точные цифры, лучше подключить менеджера — соберёт все детали и подготовит предложение. Удобнее созвониться или пообщаться в WhatsApp: https://wa.me/77476660065"

**НЕ повторяй эту фразу в двух ответах подряд**, если клиент сам не просит.

***

## 7. ОТВЕТЫ НА ТИПОВЫЕ ВОПРОСЫ

"Какие услуги вы оказываете?"
"Делаем сайты, приложения, дизайн, чат-боты с искусственным интеллектом и настройка СРМ."

"Сколько стоит?"
"Зависит от задачи. Лендинг примерно 150–300к, приложение от 400к."

"Сколько времени займёт?"
"Простой сайт 2–3 недели, магазин 1–2 месяца, приложение 1–3 месяца. Какие у вас сроки?"

"Есть рассрочка?"
"Да, можем разбить оплату по этапам. Конкретные условия зависят от проекта."

"Есть портфолио?"
"Да, есть проекты в разных нишах. Менеджер скинет примеры."

"На каких технологиях делаете?"
"Используем современные стеки под задачу. Есть конкретные требования по технологиям?"

"Делаете умных чат-ботов?"
"Да, делаем. Боты могут консультировать, подтягивать данные из CRM, помогать с заявками."

***

## 8. КОГДА КЛИЕНТ СПРАШИВАЕТ ПРО АРХИТЕКТУРУ И СТЕК

Если спрашивает прямо "на чём пишете", "как устроено":
"По архитектуре: отдельно канал (Instagram, WhatsApp и т.д.), отдельно логика бота и интеграции. По стеку Node.js или Python плюс GPT."

**Правило:**
- Коротко по делу (максимум 60 слов)
- Без лишней технички про Docker, Kubernetes, микросервисы
- Если клиент просит глубже — максимум 2–3 предложения, потом переводишь в пользу для бизнеса
- Если нужно больше — предложи менеджера

***

## 9. СЦЕНАРИИ ОТВЕТОВ

**Клиент сомневается:**
"Понимаю, выбор не простой. Можем разобрать ваш кейс, показать варианты."

**Клиент спрашивает про конкурентов:**
"Мы делаем полный цикл: от идеи и дизайна до запуска и поддержки. Всегда подстраиваемся под конкретный проект."

**Маленький бюджет:**
"Можно идти поэтапно: сначала минимальная версия с основным функционалом, потом расширение. Так вписаться в бюджет проще."

**Клиент торопится:**
"Ускориться можем, но надо выделить, что критично к запуску, а что потом добавить."

***

## 10. РЕАКЦИИ НА КОНТЕНТ В ДИРЕКТЕ

**Клиент скинул мем или прикол:**
"Используй смайлы"

**Клиент скинул пост не по теме:**
"Используй смайлы"

**Клиент шлёт стикеры, одни эмодзи:**
- Ответь смайлом в тон

**Клиент написал \"ок\", \"понятно\", \"спасибо\":**
"Отлично! Если понадобится — пишите."

После этого не продолжаешь диалог первым.

***

## 11. ВАЖНЫЕ ПРАВИЛА ПОВЕДЕНИЯ

**ДЕЛАЕШЬ:**
- Один смысл в одном сообщении
- Даёшь ответ на суть, потом (если нужно) спрашиваешь
- Естественно завершаешь разговор, если человек явно уходит
- Соблюдаешь лимит 60 слов или 300 символов

**НЕ ДЕЛАЕШЬ:**
- Не повторяешь одну фразу про менеджера в двух ответах подряд
- Не задаёшь подряд 2–3 вопроса (только ОДИН!)
- Не навязываешь "хотите консультацию" в каждом ответе
- Не тягаешь диалог, если человек завершает его
- Не пишешь "давайте разберемся с вашим проектом" на каждый вопрос
- Не говоришь "мы можем сделать сайт/приложение/бот" после каждого сообщения
- НЕ пишешь длинные ответы (больше 60 слов)

***

## 12. ПРАВИЛА ФОРМАТИРОВАНИЯ (Instagram Direct)

**Не используешь:**
- Звёздочки для выделения текста
- Решётки (#) и заголовки
- Списки с тире, точками или дефисами в начале строк
- Стрелки и псевдографика
- Угловые скобки < >, квадратные скобки [ ] без необходимости
- Сложные кавычки

**Используешь:**
- Обычный текст, как в личной переписке
- Запятые, точки, восклицательные и вопросительные знаки
- Обычные кавычки " "
- Максимум один смайл в сообщении
- Короткие предложения

**Пример хорошего ответа:**
"Делаем сайты, приложения и ботов."

**Пример плохого ответа:**
"Наша компания Jetu оказывает следующие услуги с длинным форматированием и навязчивым призывом."

***

## 13. КРИТИЧНЫЙ МОМЕНТ: НЕ НАВЯЗЫВАЕШЬ УСЛУГИ
- Слушаешь и отвечаешь на вопрос
- Если клиент явно интересуется — предлагаешь следующий шаг
- Не заканчиваешь каждый ответ продающей фразой
- Если человек просто спрашивает что-то — отвечаешь полностью

**Пример правильного диалога:**
- Клиент: "Сколько стоит сайт?"
- Ты: "Зависит от типа. Лендинг примерно 150–300к, интернет-магазин от 400к."
- Клиент: "Интернет-магазин для продажи одежды"

***

## 14. ПОСЛЕДОВАТЕЛЬНОСТЬ ДЕЙСТВИЙ В ДИАЛОГЕ

Клиент пишет
Ты понимаешь суть
Ты отвечаешь на суть (максимум 60 слов)
Если нужно уточнить — ОДИН вопрос
Клиент отвечает
Собирается ли достаточно инфо?
- ДА: Подключить менеджера (или спросить следующий шаг)
- НЕТ: Ещё ОДИН уточняющий вопрос (не несколько сразу)

**Помни:** Всегда только ОДИН вопрос за раз.

***

## 15. ПОМНИ

- Ты консультант, не продавец, который давит на кнопку
- Первая задача — понять, что нужно человеку
- Вторая задача — дать полезный ответ (коротко, максимум 60 слов)
- Третья задача (если релевантно) — предложить менеджера
- Каждый ответ должен звучать естественно, как от живого человека
- ВСЕГДА задавай только ОДИН вопрос за раз
- ВСЕГДА соблюдай лимит 60 слов или 300 символов`;

  const scenarios = [
  {
    id: 'beauty-salon',
    title: 'Салон красоты',
    description: 'Автоматическая запись клиентов, напоминания о визитах и управление расписанием мастеров',
    icon: 'Sparkles',
    image: "/images/demo_image_1.webp",
    imageAlt: 'Modern beauty salon interior with styling chairs and mirrors in bright natural lighting',
    badge: 'Популярно'
  },
  {
    id: 'real-estate',
    title: 'Недвижимость',
    description: 'Квалификация лидов, организация просмотров и автоматические ответы на типовые вопросы',
    icon: 'Building2',
    image: "/images/demo_image_2.webp",
    imageAlt: 'Luxury modern house exterior with large windows and contemporary architecture at sunset',
    badge: 'Новое'
  },
  {
    id: 'delivery',
    title: 'Доставка',
    description: 'Отслеживание заказов, уведомления о статусе и обработка запросов клиентов 24/7',
    icon: 'Truck',
    image: "/images/demo_image_3.webp",
    imageAlt: 'Delivery person in uniform holding cardboard package standing next to delivery van',
    badge: null
  },
  {
    id: 'ecommerce',
    title: 'Интернет-магазин',
    description: 'Консультации по товарам, обработка заказов и персонализированные рекомендации',
    icon: 'ShoppingCart',
    image: "/images/demo_image_4.webp",
    imageAlt: 'Shopping cart filled with colorful product boxes on laptop keyboard representing online shopping',
    badge: null
  }];


  const integrations = [
  {
    name: 'WhatsApp Business',
    logo: "/images/integration_whatsapp.webp",
    logoAlt: 'WhatsApp green speech bubble logo with white phone icon',
    status: 'Активно',
    description: 'Прямая интеграция с WhatsApp Business API для мгновенных ответов',
    isConnected: true
  },
  {
    name: 'Instagram',
    logo: "/images/integration_instagram.webp",
    logoAlt: 'Instagram gradient camera icon logo in pink purple and orange colors',
    status: 'Активно',
    description: 'Автоматические ответы в Direct Messages и комментариях',
    isConnected: true
  },
  {
    name: 'Telegram',
    logo: "/images/demo_logo_3.webp",
    logoAlt: 'Telegram blue paper plane logo on white circular background',
    status: 'Доступно',
    description: 'Telegram Bot API для создания интерактивных чат-ботов',
    isConnected: false
  },
  {
    name: 'Facebook Messenger',
    logo: "/images/rocket_gen_img_1bf61e57a-1764426212299.webp",
    logoAlt: 'Facebook Messenger blue gradient speech bubble logo with lightning bolt',
    status: 'Доступно',
    description: 'Интеграция с Facebook Messenger для расширенного охвата',
    isConnected: false
  }];


  const demoMetrics = [
  { icon: 'MessageSquare', label: 'Сообщений обработано', value: '2,847', unit: '', change: 23 },
  { icon: 'Clock', label: 'Среднее время ответа', value: '1.2', unit: 'сек', change: -15 },
  { icon: 'Users', label: 'Активных диалогов', value: '156', unit: '', change: 12 },
  { icon: 'TrendingUp', label: 'Конверсия', value: '34', unit: '%', change: 8 }];


  const quickActions = [
  { icon: 'Calendar', label: 'Записаться на услугу', variant: 'default' },
  { icon: 'Info', label: 'Узнать цены', variant: 'default' },
  { icon: 'MapPin', label: 'Адрес и контакты', variant: 'default' },
  { icon: 'Clock', label: 'Часы работы', variant: 'default' }];


  const sampleCode = `// Интеграция Jetu AI с WhatsApp Business API
import { JetuAI } from '@jetu/sdk';

const jetu = new JetuAI({
  apiKey: process.env.JETU_API_KEY,
  language: 'ru'
});

// Обработка входящих сообщений
jetu.on('message', async (message) => {
  const response = await jetu.processMessage({
    text: message.text,
    context: {
      scenario: 'beauty-salon',
      userId: message.from
    }
  });
  
  await jetu.sendMessage({
    to: message.from,
    text: response.text,
    buttons: response.quickReplies
  });
});

// Автоматическое бронирование
jetu.registerIntent('book_appointment', async (params) => {
  const booking = await createBooking({
    service: params.service,
    date: params.date,
    time: params.time
  });
  
  return {
    success: true,
    bookingId: booking.id,
    confirmation: booking.confirmationCode
  };
});`;

  const scenarioMessages = {
    'beauty-salon': [
    {
      id: 1,
      message: 'Здравствуйте! Я виртуальный ассистент салона красоты "Элегант". Чем могу помочь?',
      isUser: false,
      timestamp: '14:15',
      avatar: "/images/rocket_gen_img_11094aa78-1763296768439.webp",
      avatarAlt: 'Professional female assistant with friendly smile wearing elegant business attire in modern office setting'
    },
    {
      id: 2,
      message: 'Хочу записаться на стрижку',
      isUser: true,
      timestamp: '14:16'
    },
    {
      id: 3,
      message: 'Отлично! У нас работают опытные мастера. На какую дату вы хотели бы записаться?\n\nДоступные даты:\n• Сегодня, 29 ноября - 16:00, 18:30\n• Завтра, 30 ноября - 10:00, 14:00, 17:00\n• 1 декабря - 11:00, 15:30',
      isUser: false,
      timestamp: '14:16',
      avatar: "/images/rocket_gen_img_11094aa78-1763296768439.webp",
      avatarAlt: 'Professional female assistant with friendly smile wearing elegant business attire in modern office setting'
    },
    {
      id: 4,
      message: 'Завтра в 14:00 подойдет',
      isUser: true,
      timestamp: '14:17'
    },
    {
      id: 5,
      message: 'Превосходно! Записываю вас на 30 ноября в 14:00.\n\n✅ Услуга: Женская стрижка\n📅 Дата: 30 ноября 2025\n⏰ Время: 14:00\n💰 Стоимость: 2,500 ₸\n👤 Мастер: Анна Петрова\n\nЗа час до визита отправлю напоминание. Если планы изменятся, можете отменить или перенести запись в любое время.',
      isUser: false,
      timestamp: '14:17',
      avatar: "/images/rocket_gen_img_11094aa78-1763296768439.webp",
      avatarAlt: 'Professional female assistant with friendly smile wearing elegant business attire in modern office setting'
    }],

    'real-estate': [
    {
      id: 1,
      message: 'Добрый день! Я помощник агентства недвижимости "Премиум Хаус". Помогу подобрать идеальную квартиру. Что вас интересует?',
      isUser: false,
      timestamp: '14:15',
      avatar: "/images/rocket_gen_img_1ac12a925-1763296568790.webp",
      avatarAlt: 'Professional male real estate agent in dark suit with confident smile standing in modern office lobby'
    },
    {
      id: 2,
      message: 'Ищу двухкомнатную квартиру в центре',
      isUser: true,
      timestamp: '14:16'
    },
    {
      id: 3,
      message: 'Отличный выбор! У нас есть несколько вариантов в центральном районе.\n\nУточните, пожалуйста:\n• Бюджет: до какой суммы рассматриваете?\n• Этаж: есть ли предпочтения?\n• Ремонт: с ремонтом или без?\n• Срочность: когда планируете переезд?',
      isUser: false,
      timestamp: '14:16',
      avatar: "/images/rocket_gen_img_1ac12a925-1763296568790.webp",
      avatarAlt: 'Professional male real estate agent in dark suit with confident smile standing in modern office lobby'
    },
    {
      id: 4,
      message: 'До 30 млн, с ремонтом, не первый и не последний этаж',
      isUser: true,
      timestamp: '14:17'
    },
    {
      id: 5,
      message: 'Нашел 3 подходящих варианта:\n\n🏢 ЖК "Изумруд"\n📍 ул. Абая, 150\n💰 28,5 млн ₸ | 65 м² | 7/12 этаж\n✨ Евроремонт, мебель, техника\n\n🏢 ЖК "Премьер"\n📍 пр. Достык, 89\n💰 29,8 млн ₸ | 68 м² | 5/9 этаж\n✨ Дизайнерский ремонт\n\n🏢 ЖК "Астана"\n📍 ул. Сатпаева, 45\n💰 27,2 млн ₸ | 62 м² | 6/10 этаж\n✨ Современный ремонт\n\nОрганизовать просмотр?',
      isUser: false,
      timestamp: '14:18',
      avatar: "/images/rocket_gen_img_1ac12a925-1763296568790.webp",
      avatarAlt: 'Professional male real estate agent in dark suit with confident smile standing in modern office lobby'
    }],

    'delivery': [
    {
      id: 1,
      message: 'Здравствуйте! Служба доставки "Быстрый курьер" на связи. Чем могу помочь?',
      isUser: false,
      timestamp: '14:15',
      avatar: "/images/demo_avatar.webp",
      avatarAlt: 'Friendly delivery person in blue uniform with cap smiling while holding clipboard near delivery truck'
    },
    {
      id: 2,
      message: 'Где мой заказ #BK-2847?',
      isUser: true,
      timestamp: '14:16'
    },
    {
      id: 3,
      message: 'Проверяю статус заказа #BK-2847...\n\n📦 Статус: В пути к вам\n🚚 Курьер: Алексей М.\n📍 Текущее местоположение: ул. Розыбакиева\n⏱️ Ожидаемое время: 14:45 (через 30 минут)\n\n📱 Телефон курьера: +7 (777) 123-45-67\n\nКурьер свяжется с вами за 5 минут до прибытия. Отслеживать заказ в реальном времени можно по ссылке: track.bk.kz/2847',
      isUser: false,
      timestamp: '14:16',
      avatar: "/images/demo_avatar.webp",
      avatarAlt: 'Friendly delivery person in blue uniform with cap smiling while holding clipboard near delivery truck'
    },
    {
      id: 4,
      message: 'Спасибо! А можно изменить адрес доставки?',
      isUser: true,
      timestamp: '14:17'
    },
    {
      id: 5,
      message: 'Конечно! Так как заказ уже в пути, свяжу вас с курьером для согласования нового адреса.\n\nУкажите новый адрес доставки, и я передам информацию курьеру:\n\n📍 Текущий адрес: ул. Абая, 45, кв. 12\n📍 Новый адрес: ?',
      isUser: false,
      timestamp: '14:17',
      avatar: "/images/demo_avatar.webp",
      avatarAlt: 'Friendly delivery person in blue uniform with cap smiling while holding clipboard near delivery truck'
    }],

    'ecommerce': [
    {
      id: 1,
      message: 'Добро пожаловать в интернет-магазин "ТехноМир"! Я помогу найти нужный товар и оформить заказ. Что вас интересует?',
      isUser: false,
      timestamp: '14:15',
      avatar: "/images/rocket_gen_img_1789c8db1-1763294669212.webp",
      avatarAlt: 'Professional female customer service representative with headset smiling in modern call center office'
    },
    {
      id: 2,
      message: 'Нужен ноутбук для работы',
      isUser: true,
      timestamp: '14:16'
    },
    {
      id: 3,
      message: 'Отлично! Помогу подобрать оптимальный вариант.\n\nУточните параметры:\n• Бюджет: какую сумму готовы потратить?\n• Задачи: офисная работа, дизайн, программирование?\n• Диагональ: 13", 15" или 17"?\n• Производитель: есть предпочтения?',
      isUser: false,
      timestamp: '14:16',
      avatar: "/images/rocket_gen_img_1789c8db1-1763294669212.webp",
      avatarAlt: 'Professional female customer service representative with headset smiling in modern call center office'
    },
    {
      id: 4,
      message: 'До 300 тысяч, для офисной работы и видеоконференций, 15 дюймов',
      isUser: true,
      timestamp: '14:17'
    },
    {
      id: 5,
      message: 'Подобрал 3 лучших варианта:\n\n💻 ASUS VivoBook 15\n💰 245,000 ₸\n⚡ Intel Core i5, 16GB RAM, 512GB SSD\n⭐ 4.7/5 (234 отзыва)\n✅ В наличии, доставка завтра\n\n💻 Lenovo IdeaPad 5\n💰 268,000 ₸\n⚡ AMD Ryzen 5, 16GB RAM, 512GB SSD\n⭐ 4.8/5 (189 отзывов)\n✅ В наличии, доставка завтра\n\n💻 HP Pavilion 15\n💰 289,000 ₸\n⚡ Intel Core i7, 16GB RAM, 1TB SSD\n⭐ 4.6/5 (156 отзывов)\n✅ В наличии, доставка сегодня\n\nВсе модели идеально подходят для офисной работы и видеоконференций. Какой вариант рассмотрим подробнее?',
      isUser: false,
      timestamp: '14:18',
      avatar: "/images/rocket_gen_img_1789c8db1-1763294669212.webp",
      avatarAlt: 'Professional female customer service representative with headset smiling in modern call center office'
    }]

  };

  useEffect(() => {
    setMessages(scenarioMessages?.[activeScenario] || []);
    setConversationHistory([]);
    setIsQuickActionMessage(false);
    setShowWelcomeMessage(false);
    setWelcomeMessageVisible(false);
  }, [activeScenario]);

  useEffect(() => {
    scrollToBottom();
  }, [messages, isTyping]);

  // Intersection Observer для приветственного сообщения
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !showWelcomeMessage && messages.length === 0) {
            setShowWelcomeMessage(true);
            setTimeout(() => {
              setWelcomeMessageVisible(true);
            }, 300);
          }
        });
      },
      { threshold: 0.3 }
    );

    if (chatRef.current) {
      observer.observe(chatRef.current);
    }

    return () => {
      if (chatRef.current) {
        observer.unobserve(chatRef.current);
      }
    };
  }, [showWelcomeMessage, messages.length]);

  const scrollToChatIfOffscreen = () => {
    setTimeout(() => {
      const el = document.getElementById('demo-chat');
      if (el) {
        const rect = el.getBoundingClientRect();
        // If the chat is mostly off the screen, scroll back to it
        if (rect.bottom < 100 || rect.top > window.innerHeight - 100) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }
    }, 500);
  };

  const scrollToBottom = () => {
    if (chatRef.current) {
      chatRef.current.scrollTo({
        top: chatRef.current.scrollHeight,
        behavior: 'smooth'
      });
    }
  };

  useEffect(() => {
    if (window.location.hash === '#demo-chat') {
      setTimeout(() => {
        document.getElementById('demo-chat')?.scrollIntoView({ behavior: 'smooth' });
      }, 500);
    }
  }, []);

  const handleSendMessage = async () => {
    if (!inputMessage?.trim()) return;

    const newMessage = {
      id: messages?.length + 1,
      message: inputMessage,
      isUser: true,
      timestamp: new Date()?.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })
    };

    setMessages([...messages, newMessage]);
    const currentInput = inputMessage;
    setInputMessage('');
    setIsTyping(true);

    try {
      // Check if this is a quick action message (predefined response scenario)
      const isQuickAction = quickActions?.some(action => action?.label === currentInput);
      
      let botResponseText;
      
      if (isQuickAction || isQuickActionMessage) {
        // Use predefined responses for quick actions
        const responses = {
          'beauty-salon': 'Спасибо за ваш вопрос! Наш администратор свяжется с вами в ближайшее время для уточнения деталей.',
          'real-estate': 'Отличный вопрос! Я подготовлю для вас детальную информацию и свяжусь в течение 15 минут.',
          'delivery': 'Понял вас! Проверяю информацию в системе и сразу предоставлю актуальные данные.',
          'ecommerce': 'Благодарю за интерес! Сейчас подберу для вас наилучшие варианты с учетом ваших пожеланий.'
        };
        botResponseText = responses?.[activeScenario] || 'Спасибо за ваше сообщение! Обрабатываю запрос...';
        
        // Update conversation history for context (even for quick actions)
        setConversationHistory([
          ...conversationHistory,
          { message: currentInput, isUser: true },
          { message: botResponseText, isUser: false }
        ]);
        
        // Reset quick action flag after processing, so next messages use AI
        setIsQuickActionMessage(false);
      } else {
        // Use OpenAI for keyboard-typed messages
        const history = conversationHistory?.map(msg => ({
          role: msg?.isUser ? 'user' : 'assistant',
          content: msg?.message
        }));

        botResponseText = await getChatbotResponse(currentInput, history);
        
        // Update conversation history for context
        setConversationHistory([
          ...conversationHistory,
          { message: currentInput, isUser: true },
          { message: botResponseText, isUser: false }
        ]);
        
        // Ensure quick action flag is false for AI responses
        setIsQuickActionMessage(false);
      }

      const botResponse = {
        id: messages?.length + 2,
        message: botResponseText,
        isUser: false,
        timestamp: new Date()?.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' }),
        avatar: scenarioMessages?.[activeScenario]?.[0]?.avatar,
        avatarAlt: scenarioMessages?.[activeScenario]?.[0]?.avatarAlt
      };

      setMessages((prev) => [...prev, botResponse]);
      scrollToChatIfOffscreen();
    } catch (error) {
      console.error('Error sending message:', error);
      
      const errorMessage = {
        id: messages?.length + 2,
        message: error?.message || 'Извините, произошла ошибка. Попробуйте еще раз.',
        isUser: false,
        timestamp: new Date()?.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' }),
        avatar: scenarioMessages?.[activeScenario]?.[0]?.avatar,
        avatarAlt: scenarioMessages?.[activeScenario]?.[0]?.avatarAlt
      };
      
      setMessages((prev) => [...prev, errorMessage]);
      scrollToChatIfOffscreen();
    } finally {
      setIsTyping(false);
    }
  };

  const handleQuickAction = (label) => {
    setInputMessage(label);
    setIsQuickActionMessage(true);
  };

  const handleScenarioChange = (scenarioId) => {
    setActiveScenario(scenarioId);
    setMessages([]);
    setInputMessage('');
    setConversationHistory([]);
    setIsQuickActionMessage(false);
  };

  const languageOptions = [
  { value: 'ru', label: 'Русский' },
  { value: 'kk', label: 'Қазақша' },
  { value: 'en', label: 'English' }];


  return (
    <>
      <Helmet>
        <title>Демо-площадка | Jetu AI Platform</title>
        <meta name="description" content="Протестируйте возможности Jetu AI в реальном времени. Интерактивная демонстрация автоматизации для различных бизнес-сценариев." />
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header />

        <main className="pt-20 lg:pt-24">
          <section className="py-12 lg:py-16 bg-gradient-to-br from-primary via-secondary to-primary">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto text-center">
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-accent/10 border border-accent/30 mb-6">
                  <Icon name="Zap" size={16} color="var(--color-accent)" />
                  <span className="text-sm font-medium text-accent">Интерактивная демонстрация</span>
                </div>

                <h1 className="text-4xl lg:text-5xl font-headline font-bold text-white mb-6">
                  Протестируйте Jetu AI
                  <span className="block text-accent mt-2">в действии</span>
                </h1>

                <p className="text-lg text-gray-200 mb-8 max-w-2xl mx-auto">
                  Выберите сценарий использования и испытайте возможности искусственного интеллекта Jetu в реальном времени. Никаких регистраций — просто начните диалог.
                </p>

                <div className="flex flex-wrap items-center justify-center gap-4">
                  <Button
                    variant="default"
                    size="lg"
                    iconName="Play"
                    iconPosition="left"
                    onClick={() => document.getElementById('demo-chat')?.scrollIntoView({ behavior: 'smooth' })}>
                    Начать тестирование
                  </Button>
                </div>
              </div>
            </div>
          </section>

          <section className="py-12 lg:py-16">
            <div className="container mx-auto px-4">
              <MetricsDisplay metrics={demoMetrics} />
            </div>
          </section>

          <section className="py-12 lg:py-16 bg-card">
            <div className="container mx-auto px-4">
              <div className="max-w-6xl mx-auto">
                <div className="text-center mb-12">
                  <h2 className="text-3xl lg:text-4xl font-headline font-bold text-foreground mb-4">
                    Выберите бизнес-сценарий
                  </h2>
                  <p className="text-muted-foreground max-w-2xl mx-auto">
                    Каждый сценарий демонстрирует специализированные возможности AI для конкретной отрасли
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-12">
                  {scenarios?.map((scenario) =>
                  <ScenarioCard
                    key={scenario?.id}
                    title={scenario?.title}
                    description={scenario?.description}
                    icon={scenario?.icon}
                    image={scenario?.image}
                    imageAlt={scenario?.imageAlt}
                    badge={scenario?.badge}
                    isActive={activeScenario === scenario?.id}
                    onClick={() => handleScenarioChange(scenario?.id)} />

                  )}
                </div>

                {showCode &&
                <div className="mb-12 animate-fade-in">
                    <CodePreview code={sampleCode} language="javascript" />
                  </div>
                }

                <div className="grid lg:grid-cols-3 gap-8">
                  <div className="lg:col-span-2">
                    <div 
                      id="demo-chat" 
                      className={`bg-background border-border shadow-xl overflow-hidden flex flex-col transition-all duration-300 ${
                        isExpanded ? 'fixed inset-0 z-[100] h-[100dvh] w-full rounded-none border-0' : 'scroll-mt-32 rounded-2xl border'
                      }`}
                    >
                      <div className="bg-gradient-to-r from-accent to-primary p-4 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
                            <Icon name="Bot" size={20} color="#FFFFFF" />
                          </div>
                          <div>
                            <h3 className="font-semibold text-white">Jetu AI Assistant</h3>
                            <div className="flex items-center gap-1">
                              <div className="w-2 h-2 rounded-full bg-success animate-pulse" />
                              <span className="text-xs text-white/80">Онлайн</span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center">
                          <Select
                            options={languageOptions}
                            value={selectedLanguage}
                            onChange={setSelectedLanguage}
                            className="w-32" />
                          {isExpanded && (
                            <button 
                              className="md:hidden p-2 text-white/80 hover:text-white hover:bg-white/20 rounded-full ml-1 transition-colors"
                              onClick={(e) => { e.stopPropagation(); setIsExpanded(false); }}
                            >
                              <Icon name="X" size={20} color="currentColor" />
                            </button>
                          )}
                        </div>
                      </div>

                      <div ref={chatRef} className={`overflow-y-auto p-4 md:p-6 bg-muted/30 ${isExpanded ? 'flex-1' : 'h-[400px] md:h-[500px]'}`}>
                        {showWelcomeMessage && welcomeMessageVisible && messages.length === 0 && (
                          <div className="flex justify-start animate-fade-in mb-4">
                            <div className="max-w-[80%] px-4 py-3 rounded-2xl bg-card border border-border rounded-bl-none animate-slide-up">
                              <div className="flex items-center gap-2 mb-2">
                                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-accent to-primary flex items-center justify-center">
                                  <Icon name="Bot" size={16} color="#FFFFFF" />
                                </div>
                                <span className="text-sm font-semibold text-foreground">Jetu AI Assistant</span>
                              </div>
                              <p className="text-sm leading-relaxed text-foreground">
                                Привет! 👋 Я AI-ассистент Jetu AI. Готов помочь вам с вопросами о разработке сайтов, мобильных приложений и автоматизации бизнеса. Чем могу помочь?
                              </p>
                            </div>
                          </div>
                        )}
                        {messages?.map((msg) =>
                        <ChatMessage
                          key={msg?.id}
                          message={msg?.message}
                          isUser={msg?.isUser}
                          timestamp={msg?.timestamp}
                          avatar={msg?.avatar}
                          avatarAlt={msg?.avatarAlt} />

                        )}
                        {isTyping && <TypingIndicator />}
                        <div ref={messagesEndRef} />
                      </div>

                      <div className="p-4 bg-card border-t border-border">
                        <div className="flex flex-wrap gap-2 mb-3">
                          {quickActions?.map((action, index) =>
                          <QuickActionButton
                            key={index}
                            icon={action?.icon}
                            label={action?.label}
                            variant={action?.variant}
                            onClick={() => handleQuickAction(action?.label)} />

                          )}
                        </div>

                        <div className="flex gap-2">
                          <Input
                            type="text"
                            placeholder="Введите ваше сообщение..."
                            value={inputMessage}
                            onChange={(e) => setInputMessage(e?.target?.value)}
                            onKeyPress={(e) => e?.key === 'Enter' && handleSendMessage()}
                            onFocus={() => { if (window.innerWidth < 768) setIsExpanded(true); }}
                            className="flex-1" />

                          <Button
                            variant="default"
                            size="lg"
                            iconName="Send"
                            onClick={handleSendMessage}
                            disabled={!inputMessage?.trim()}>

                            Отправить
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-6">
                    <div className="bg-card rounded-xl border border-border p-6">
                      <div className="flex items-center gap-2 mb-4">
                        <Icon name="Plug" size={20} color="var(--color-accent)" />
                        <h3 className="font-headline font-semibold text-foreground">Интеграции</h3>
                      </div>
                      <div className="space-y-3">
                        {integrations?.map((integration, index) =>
                        <IntegrationPreview
                          key={index}
                          name={integration?.name}
                          logo={integration?.logo}
                          logoAlt={integration?.logoAlt}
                          status={integration?.status}
                          description={integration?.description}
                          isConnected={integration?.isConnected} />

                        )}
                      </div>
                    </div>

                    <div className="bg-gradient-to-br from-accent/10 to-primary/10 rounded-xl border border-accent/30 p-6">
                      <div className="flex items-center gap-2 mb-4">
                        <Icon name="Sparkles" size={20} color="var(--color-accent)" />
                        <h3 className="font-headline font-semibold text-foreground">Возможности AI</h3>
                      </div>
                      <ul className="space-y-3">
                        <li className="flex items-start gap-2">
                          <Icon name="Check" size={16} color="var(--color-success)" className="mt-1 flex-shrink-0" />
                          <span className="text-sm text-foreground">Обработка естественного языка</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <Icon name="Check" size={16} color="var(--color-success)" className="mt-1 flex-shrink-0" />
                          <span className="text-sm text-foreground">Контекстное понимание запросов</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <Icon name="Check" size={16} color="var(--color-success)" className="mt-1 flex-shrink-0" />
                          <span className="text-sm text-foreground">Автоматическое бронирование</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <Icon name="Check" size={16} color="var(--color-success)" className="mt-1 flex-shrink-0" />
                          <span className="text-sm text-foreground">Персонализированные ответы</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <Icon name="Check" size={16} color="var(--color-success)" className="mt-1 flex-shrink-0" />
                          <span className="text-sm text-foreground">Работа 24/7 без перерывов</span>
                        </li>
                      </ul>
                    </div>

                    <div className="bg-card rounded-xl border border-border p-6">
                      <div className="flex items-center gap-2 mb-4">
                        <Icon name="Shield" size={20} color="var(--color-success)" />
                        <h3 className="font-headline font-semibold text-foreground">Безопасность</h3>
                      </div>
                      <div className="space-y-3">
                        <div className="flex items-center gap-2">
                          <Icon name="Lock" size={16} color="var(--color-success)" />
                          <span className="text-sm text-muted-foreground">Шифрование данных</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Icon name="Shield" size={16} color="var(--color-success)" />
                          <span className="text-sm text-muted-foreground">GDPR совместимость</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Icon name="CheckCircle2" size={16} color="var(--color-success)" />
                          <span className="text-sm text-muted-foreground">ISO 27001 сертификация</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="py-12 lg:py-16 bg-gradient-to-br from-accent/5 to-primary/5">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto text-center">
                <h2 className="text-3xl lg:text-4xl font-headline font-bold text-foreground mb-6">
                  Готовы внедрить AI в ваш бизнес?
                </h2>
                <p className="text-lg text-muted-foreground mb-8">
                  Начните бесплатный пробный период на 3 дня. Без кредитной карты. Полный доступ ко всем функциям.
                </p>
                <div className="flex flex-wrap items-center justify-center gap-4">
                  <Button
                    variant="default"
                    size="lg"
                    iconName="Rocket"
                    iconPosition="left"
                    onClick={() => window.location.href = '/pricing-plans'}>

                    Начать бесплатно
                  </Button>
                  <Button
                    variant="outline"
                    size="lg"
                    iconName="MessageSquare"
                    iconPosition="left"
                    onClick={() => window.location.href = '/contact-support'}>

                    Связаться с нами
                  </Button>
                </div>
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>);

};

export default DemoPlayground;